// eslint-disable-next-line no-restricted-imports
import {useChatState} from '@github-ui/copilot-chat/CopilotChatContext'
import {sendEvent} from '@github-ui/hydro-analytics'
import {getSafeConfig} from '@github-ui/issue-create/getSafeConfig'
import {IssueCreateContextProvider} from '@github-ui/issue-create/IssueCreateContext'
import type {PreselectedDataTypes} from '@github-ui/issue-create/IssueCreateDataContext'
import type {IssueFormRef} from '@github-ui/issue-form/Types'
import {IssueViewerLoading} from '@github-ui/issue-viewer/IssueViewerLoading'
import type {RepositoryPickerRepository} from '@github-ui/item-picker/RepositoryPicker'
import {testIdProps} from '@github-ui/test-id-props'
import {IssueDraftIcon, RepoIcon} from '@primer/octicons-react'
import {Banner, Stack} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore} from 'react'

import {BROWSER_ANIMATION_DURATION} from '../../../utils/constants'
import type {DraftIssue} from '../content-preview-types'
import {FailureState} from '../FailureState'
import filePreviewStyles from '../FilePreview.module.css'
import issuePreviewStyles from '../IssuePreview.module.css'
import {VersionSelector} from '../VersionSelector'
import {CreateIssueForm} from './CreateIssueForm'
import styles from './CreateIssuePreview.module.css'
import {TemplatePicker} from './TemplatePicker'
import type {OnUpdateProps, ProjectUpdateError} from './update-issue-mutation'
import {UpdateIssueButton} from './UpdateIssueButton'
import {useOnChangeCallback} from './use-on-change-callback'
import {useOnFieldChangeCallback} from './use-on-field-change-callback'
import type {ContinuableError, SavedIssue} from './use-on-save-callback'
import {useOnSaveCallback} from './use-on-save-callback'
import {useRepoQuery} from './use-repo-query'
import {useDraftIssueFormTemplateQuery} from './use-template-query'
import {useTransformCopilotImageURLsFunction} from './use-transform-copilot-image-urls-function'

export const METADATA_FOOTER_BREAKPOINT = 1024

export type UpdateIssuePreviewProps = {
  issue: DraftIssue
  isPreviewOpening: boolean
  onClose: () => void
}

export function UpdateIssuePreview({isPreviewOpening, issue: updateIssue, onClose}: UpdateIssuePreviewProps) {
  // There are rendering issues if we draw the form while animating the workbench opening, so we show loading until the animation is done.
  const [isAnimating, setIsAnimating] = useState(isPreviewOpening)
  const [updateErrors, setUpdateErrors] = useState<Error[] | null>(null)
  const [prevIssueId, setPrevIssueId] = useState(updateIssue.id)

  useEffect(() => {
    if (isAnimating) {
      const timer = setTimeout(() => setIsAnimating(false), BROWSER_ANIMATION_DURATION)
      return () => clearTimeout(timer)
    }
  }, [isAnimating])

  // Reset updateErrors when switching to a different issue
  if (prevIssueId !== updateIssue.id) {
    setPrevIssueId(updateIssue.id)
    setUpdateErrors(null)
  }

  const {selectedThreadID} = useChatState()

  const isRepositoryStreamed = updateIssue.repository != null || !updateIssue.isStreaming
  const isIssueBodyVersionStreamed = updateIssue.bodyVersion != null || !updateIssue.isStreaming
  const isMalformedDraft = isIssueBodyVersionStreamed && !updateIssue.bodyVersion

  // Load repository data
  const [owner, repo] = updateIssue.repository?.split('/') ?? []
  const {isLoading: loadingIssueRepo, data: issueRepo} = useRepoQuery({owner, repo})
  const {isLoading: loadingTemplate, data: templateData} = useDraftIssueFormTemplateQuery({
    repoId: issueRepo?.id ?? '',
    filename: updateIssue.template || '',
  })

  const noRepoAccess = useMemo(() => {
    return (
      issueRepo != null &&
      (!issueRepo.hasIssuesEnabled ||
        (issueRepo.viewerInteractionLimitReasonHTML != null && issueRepo.viewerInteractionLimitReasonHTML?.length > 0))
    )
  }, [issueRepo])

  const transformCopilotImageUrls = useTransformCopilotImageURLsFunction()

  const onSaveCallback = useOnSaveCallback()
  const onUpdateCallback = useCallback(
    async (issue: SavedIssue) => {
      await onSaveCallback([issue])
    },
    [onSaveCallback],
  )

  const onUpdateError = useCallback(
    async (error: Error | OnUpdateProps) => {
      let errorType = 'OTHER'

      // Handle OnUpdateProps from button (mutations with failures)
      if (error && typeof error === 'object' && 'result' in error) {
        const {result} = error
        if (result.issue === undefined) {
          // Handle case where issue is not defined
          const unknownError = new Error('Issue data is unavailable')
          unknownError.name = 'UNKNOWN_ERROR'
          setUpdateErrors([unknownError])
          return
        }

        // Extract databaseId from result.issue when available
        const sourceIssueId = result.issue?.repository?.databaseId

        // Check if all mutations failed
        if (result.failed.length > 0 && result.successful.length === 0) {
          // All mutations failed - show error banner for each failure
          errorType = 'MULTIPLE_UPDATE_FAILURES'

          sendEvent('dotcom_chat.activate.error', {
            target: 'ERROR_ISSUE_UPDATE_FAILED',
            mode: 'immersive',
            message: `Issue update failed: ${result.failed.length} operations failed`,
            draftIssueId: updateIssue.id,
            sourceIssueId,
            repository: updateIssue.repository,
            errorType,
            failedOperationCount: result.failed.length,
            failedOperations: JSON.stringify(
              result.failed.map(f => ({operation: f.operation, error: f.error.message})),
            ),
          })

          // Create an error for each failed operation
          const errors = result.failed.map(failedOp => {
            const failureError = new Error(failedOp.error.message)
            failureError.name = `${failedOp.operation.toUpperCase()}_UPDATE_FAILED`
            ;(failureError as Error & {operation: string}).operation = failedOp.operation

            // For project operations, extract projectErrors from the original error
            if (failedOp.operation === 'projects') {
              const originalError = failedOp.error as Error & {projectErrors?: ProjectUpdateError[]}
              if (originalError.projectErrors) {
                ;(failureError as Error & {projectErrors: ProjectUpdateError[]}).projectErrors =
                  originalError.projectErrors
              }
            }

            return failureError
          })

          setUpdateErrors(errors)
        } else if (result.failed.length > 0 && result.successful.length > 0) {
          // Partial failure - continue with onSaveCallback logic with additional message
          const failedOperations = result.failed.map(f => f.operation).join(', ')
          result.issue.errors = result.failed.map(failedOp => {
            const continuableError: ContinuableError = {
              message: failedOp.error.message,
              type: 'update',
            }
            return continuableError
          })
          const successfulOperations = result.successful.join(', ')

          errorType = 'PARTIAL_UPDATE_FAILURE'
          sendEvent('dotcom_chat.activate.error', {
            target: 'ERROR_ISSUE_UPDATE_PARTIAL_FAILURE',
            mode: 'immersive',
            message: `Issue update partial failure. Failed: ${failedOperations}. Succeeded: ${successfulOperations}`,
            draftIssueId: updateIssue.id,
            sourceIssueId,
            repository: updateIssue.repository,
            errorType,
            failedOperationCount: result.failed.length,
            failedOperations: JSON.stringify(
              result.failed.map(f => ({operation: f.operation, error: f.error.message})),
            ),
            successfulOperations: JSON.stringify(result.successful),
          })

          // Continue with success flow but with warning message
          try {
            await onSaveCallback([result.issue])
          } catch {
            // Fall back to error display if we can't continue with success flow
            const partialError = new Error(`Partial failure: ${failedOperations} failed`)
            partialError.name = errorType
            setUpdateErrors([partialError])
          }
        }

        return // OnUpdateProps handling complete
      }

      // Handle standard error
      if (error instanceof Error) {
        const standardError = new Error(error.message)
        if ('name' in error) {
          standardError.name = error.name
        } else {
          standardError.name = errorType
        }

        sendEvent('dotcom_chat.activate.error', {
          target: 'ERROR_ISSUE_UPDATE_FAILED',
          mode: 'immersive',
          message: error.message,
          issueId: updateIssue.id,
          errorType,
        })

        setUpdateErrors([standardError])
      }
    },
    [updateIssue, onSaveCallback],
  )

  const onChange = useOnChangeCallback(updateIssue)
  const onFieldChange = useOnFieldChangeCallback(updateIssue, onChange)

  const containerRef = useRef<HTMLDivElement>(null)
  const renderNarrowView = useRenderNarrowView(containerRef)

  const issueFormRef = useRef<IssueFormRef>(null)

  const errorBanners = useMemo(() => {
    if (!updateErrors || updateErrors.length === 0) return null

    return updateErrors.map(error => {
      let title: string
      let description: string

      switch (error.name) {
        case 'LABELS_UPDATE_FAILED':
          title = 'Failed to update labels'
          description = 'Could not update issue labels. Please try again.'
          break
        case 'ASSIGNEES_UPDATE_FAILED':
          title = 'Failed to update assignees'
          description = 'Could not update issue assignees. Please try again.'
          break
        case 'ASSIGNCOPILOT_UPDATE_FAILED':
          title = 'Failed to update assignees'
          description = 'Could not assign Copilot to the issue. Please try again.'
          break
        case 'MILESTONE_UPDATE_FAILED':
          title = 'Failed to update milestone'
          description = 'Could not update issue milestone. Please try again.'
          break
        case 'ISSUETYPE_UPDATE_FAILED':
          title = 'Failed to update issue type'
          description = 'Could not update issue type. Please try again.'
          break
        case 'PROJECTS_UPDATE_FAILED':
          title = 'Failed to update projects'
          description = 'Could not update project assignments. Please try again.'
          break
        case 'CONTENT_UPDATE_FAILED':
          title = 'Failed to update content'
          description = 'Could not update issue title or body. Please try again.'
          break
        case 'FORBIDDEN':
          title = 'Permission Denied'
          description = "You don't have permission to update this issue. Please contact the repo owner."
          break
        case 'NOT_FOUND':
          title = 'Issue Not Found'
          description = 'The issue you are trying to update could not be found or no longer exists.'
          break
        case 'INVALID_TITLE':
          title = 'Invalid issue title'
          description = error.message!
          break
        case 'INVALID_FORM_INPUTS':
          title = 'Invalid form inputs'
          description = error.message!
          break
        case 'STALE_DATA':
          title = 'Issue Outdated'
          description =
            'The issue you are editing has changed. Please copy your edits and have Copilot draft a new update.'
          break
        case 'PROJECT_UPDATE_FAILED': {
          title = 'Failed to update project assignment'
          const projectErrors = (error as Error & {projectErrors?: ProjectUpdateError[]}).projectErrors ?? []
          if (projectErrors.length > 0) {
            const addErrors = projectErrors.filter((err: ProjectUpdateError) => err.operation === 'add')
            const removeErrors = projectErrors.filter((err: ProjectUpdateError) => err.operation === 'remove')

            const errorParts: string[] = []
            if (addErrors.length > 0) {
              errorParts.push(
                `Failed to add to ${addErrors.length} project(s): ${addErrors
                  .map((err: ProjectUpdateError) => `"${err.projectTitle}"`)
                  .join(', ')}`,
              )
            }

            if (removeErrors.length > 0) {
              errorParts.push(
                `Failed to remove ${removeErrors.length} project(s): ${removeErrors
                  .map((err: ProjectUpdateError) => `"${err.projectTitle}"`)
                  .join(', ')}`,
              )
            }

            description = `The issue was updated successfully, but there were problems with project assignments. ${errorParts.join(
              '. ',
            )}.`
          } else {
            description = 'The issue was updated successfully, but there were problems with project assignments.'
          }
          break
        }
        default:
          title = 'An error has occurred'
          description = 'Please try again later.'
          break
      }

      const variant = error.name === 'PROJECT_UPDATE_FAILED' ? 'warning' : 'critical'
      return <Banner key={`error-${error.name}`} variant={variant} title={title} description={description} />
    })
  }, [updateErrors])

  const preselectedData = useMemo<PreselectedDataTypes>(() => ({repository: issueRepo ?? undefined}), [issueRepo])

  if (isAnimating || !isRepositoryStreamed || loadingIssueRepo || loadingTemplate) {
    return <IssueViewerLoading optionConfig={{useViewportQueries: false}} />
  }

  if (!issueRepo) {
    return renderRepositoryNotFoundBlankslate(updateIssue.repository)
  }

  if (noRepoAccess) {
    return renderNoRepoAccessBlankslate(issueRepo)
  }

  if (isMalformedDraft) {
    return renderMalformedDraftBlankslate()
  }

  return (
    <IssueCreateContextProvider
      optionConfig={getSafeConfig({
        storageKeyPrefix: `${updateIssue.id}@${selectedThreadID!}`,
        insidePortal: renderNarrowView,
        disableCopilotAssignmentModal: true,
        isCopilotImmersive: true,
      })}
      preselectedData={preselectedData}
      {...testIdProps('update-issue-preview')}
    >
      <div className={clsx(issuePreviewStyles.container, styles.container)} ref={containerRef}>
        <div className={filePreviewStyles.toolbar}>
          <div className={clsx(filePreviewStyles.toolbarLeft, styles.toolbarLeft)}>
            <VersionSelector item={updateIssue} />
          </div>
          <div className={filePreviewStyles.toolbarActions}>
            <UpdateIssueButton
              issueRepo={issueRepo}
              draftIssue={updateIssue}
              template={templateData ?? undefined}
              issueFormRef={issueFormRef}
              onUpdate={onUpdateCallback}
              onUpdateError={onUpdateError}
              onClose={onClose}
            />
          </div>
        </div>
        <div className={styles.form}>
          {updateErrors && errorBanners}
          <Stack direction={'horizontal'} align={'center'} gap={'condensed'} className="py-2">
            <RepoIcon />
            <b>{`${issueRepo.owner.login}/${issueRepo.name}`}</b>
            <TemplatePicker draftIssue={updateIssue} />
          </Stack>
          <CreateIssueForm
            draftIssue={updateIssue}
            template={templateData ?? undefined}
            issueFormRef={issueFormRef}
            onChange={onChange}
            onClose={onClose}
            onBeforeCreate={issueBody => transformCopilotImageUrls(issueRepo, issueBody)}
            onFieldChange={onFieldChange}
          />
        </div>
      </div>
    </IssueCreateContextProvider>
  )
}

function renderMalformedDraftBlankslate() {
  return (
    <FailureState
      title="Malformed draft issue"
      description="The drafted issue contains malformed data and the preview is not available. Please retry."
      icon={IssueDraftIcon}
    />
  )
}

function renderNoRepoAccessBlankslate(repository: RepositoryPickerRepository) {
  if (!repository.hasIssuesEnabled) {
    return (
      <FailureState
        title="Issues are disabled"
        description="Issues are disabled on this repository. Please select a different repository above to continue updating your issue."
        icon={IssueDraftIcon}
      />
    )
  }

  if (repository.viewerInteractionLimitReasonHTML) {
    return (
      <FailureState
        title="You cannot update issues in this repository"
        description={repository.viewerInteractionLimitReasonHTML}
        icon={IssueDraftIcon}
      />
    )
  }
}

// TODO: better blankslate?
function renderRepositoryNotFoundBlankslate(repository?: string) {
  if (!repository) {
    return (
      <FailureState
        title="Malformed draft issue"
        description="The drafted issue contains malformed data and the preview is not available. Please retry."
        icon={IssueDraftIcon}
      />
    )
  }

  return (
    <FailureState
      title={`${repository} is not found`}
      description="Please verify if the repository exists and is accessible before trying again."
      icon={IssueDraftIcon}
    />
  )
}

function useRenderNarrowView<T extends HTMLElement>(elementRef: React.RefObject<T | null>) {
  const subscribe = (notify: () => void) => {
    const resizeObserver = new ResizeObserver(notify)
    resizeObserver.observe(document.documentElement)
    return () => {
      resizeObserver.unobserve(document.documentElement)
      resizeObserver.disconnect()
    }
  }

  const onChange = () => {
    if (!elementRef.current) return true

    return elementRef.current.clientWidth < METADATA_FOOTER_BREAKPOINT
  }

  return useSyncExternalStore(subscribe, onChange)
}
